import React, { useEffect, useMemo, useState } from 'react';
import useAllMerchandisers from '../../redux/composableDevice/hook/useAllMerchandisers';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import MerchandiserContainer from '../Merchandiser/MerchandiserContainer';
import { getPolyglot } from '../../../../i18n';
import { useParams } from 'react-router-dom';
import AddMerchandiserModal from '../Merchandiser/AddMerchandiserModal';
import { AddMerchandiserIcon } from '../../theme/icons';
import { StoreSubheader } from './StoreSubheader';
import {
  getGroupByIdWithFallbackSelector,
  getGroupsByWorkspaceLoadingSelector,
} from '../../../../redux/groups/selectors';
import { useAppSelector } from '../../../../redux/store.model';
import { getMyWorkspaceSelector } from '../../../../redux/stoerkID/selectors/StoerkId.selectors';

const MerchandisersPage = () => {
  const params = useParams<{ merchandiserId: string }>();
  const groupId = params.merchandiserId;
  const myWorkspaceId = useAppSelector(getMyWorkspaceSelector)?.id ?? '';

  const { merchandisers, isLoading: isLoadingMerchandisers } =
    useAllMerchandisers({
      groupId,
      workspaceId: myWorkspaceId,
    });
  const selectedGroup = useAppSelector((state) =>
    getGroupByIdWithFallbackSelector(state, {
      groupId: groupId ?? '',
      workspaceId: myWorkspaceId ?? '',
    })
  );
  const isLoadingGroupsByWorkspace = useAppSelector((state) =>
    getGroupsByWorkspaceLoadingSelector(state, { workspaceId: myWorkspaceId })
  );
  const polyglot = getPolyglot();

  // const selectedGroup = useMemo(
  //   () => groups?.find((group) => group.id === groupId),
  //   [groups, groupId]
  // );

  // Stabilize merchandisers array reference based on IDs
  const stableMerchandisers = useMemo(() => {
    return merchandisers;
  }, [
    merchandisers
      .map((m) => m.id)
      .sort()
      .join(','),
  ]);

  const sortedMerchandisers = useMemo(
    () => [...stableMerchandisers].sort((a, b) => a.name.localeCompare(b.name)),
    [stableMerchandisers]
  );

  const [openAddMerchanidserModal, setOpenAddMerchanidserModal] =
    useState(false);

  const [collapsedId, setCollapsedId] = useState<string[]>([]);

  useEffect(() => {
    if (
      sortedMerchandisers.length === 1 &&
      !collapsedId?.includes(sortedMerchandisers[0].id)
    ) {
      setCollapsedId([sortedMerchandisers[0].id]);
    }
  }, [sortedMerchandisers, collapsedId]);

  if (isLoadingMerchandisers || isLoadingGroupsByWorkspace) {
    return (
      <Container
        maxWidth="xl"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  const addButton = (
    <Container
      maxWidth="xl"
      sx={{ paddingTop: '3rem', backgroundColor: 'background.container' }}
    >
      <Button
        onClick={() => setOpenAddMerchanidserModal(true)}
        style={{ borderRadius: '1rem', marginBottom: '2rem' }}
        fullWidth
        variant="outlined"
        sx={{
          '&:hover': {
            backgroundColor: 'primary.light',
            color: 'primary.contrastText',
            border: 'none',
          },
        }}
        startIcon={<AddMerchandiserIcon />}
      >
        {polyglot.t('freshwatch.add_merchandiser')}
      </Button>
      <AddMerchandiserModal
        groupId={groupId ?? ''}
        open={openAddMerchanidserModal}
        onClose={() => setOpenAddMerchanidserModal(false)}
      />
    </Container>
  );
  const backButtonView = <StoreSubheader group={selectedGroup} />;

  let content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
        backgroundColor: 'background.default',
      }}
    >
      {sortedMerchandisers.map((merchandiser) => (
        <MerchandiserContainer
          key={merchandiser.id}
          merchandiser={merchandiser}
          collapsed={!collapsedId?.includes(merchandiser.id)}
          onCollapse={(merchandiserId) => {
            setCollapsedId((prev: string[]) => {
              if (prev?.includes(merchandiserId)) {
                return prev.filter((id) => id !== merchandiserId);
              } else {
                return prev ? [...prev, merchandiserId] : [merchandiserId];
              }
            });
          }}
        />
      ))}
    </Box>
  );

  if (!stableMerchandisers || stableMerchandisers.length === 0) {
    content = (
      <Box
        display={'flex'}
        flexDirection="column"
        alignItems="center"
        padding={3}
      >
        <Typography variant="h2">{polyglot.t('freshwatch.welcome')}</Typography>
        <Typography variant="h5" padding={2} color={'text.primary'}>
          {polyglot.t('freshwatch.add_merchandiser_description')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        maxWidth: 'xl',
        margin: '0 auto',
        backgroundColor: 'background.container',
      }}
    >
      {backButtonView}
      {content}
      {addButton}
    </Box>
  );
};
export default MerchandisersPage;
